import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminAnalyticsService } from "../../../../services/adminAnalyticsService";
import SummaryCards from "../SummaryCards";
import DataTable from "../DataTable";
import PaginationControls from "../PaginationControls";
import {
  formatDateTime,
  getFieldValue,
  getPrimaryArray,
  getSummaryEntries,
} from "../analyticsUtils";
import { SectionError, SectionLoading } from "../SectionState";

const USER_COLUMNS = [
  {
    key: "email",
    label: "Email",
    render: (_, row) => getFieldValue(row, ["email"]),
  },
  {
    key: "first_name",
    label: "First Name",
    render: (_, row) => getFieldValue(row, ["first_name", "firstName"]),
  },
  {
    key: "last_name",
    label: "Last Name",
    render: (_, row) => getFieldValue(row, ["last_name", "lastName"]),
  },
  {
    key: "created_at",
    label: "Created At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["created_at", "createdAt"])),
  },
  {
    key: "first_start_at",
    label: "First Start At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["first_start_at", "firstStartAt"])),
  },
  {
    key: "last_login_at",
    label: "Last Login At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["last_login_at", "lastLoginAt"])),
  },
  {
    key: "last_activity_at",
    label: "Last Activity At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["last_activity_at", "lastActivityAt"])),
  },
  {
    key: "days_since_signup",
    label: "Days Since Signup",
    render: (_, row) => getFieldValue(row, ["days_since_signup", "daysSinceSignup"]),
  },
];

const ActivationReportPanel = ({ paidOnly = false }) => {
  const [query, setQuery] = useState({
    days_without_start: 7,
    limit: 200,
    offset: 0,
  });
  const [draft, setDraft] = useState(query);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await adminAnalyticsService.getActivationReport({
        ...query,
        paid_only: paidOnly,
      });
      setData(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load activation report.");
    } finally {
      setLoading(false);
    }
  }, [paidOnly, query]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const users = useMemo(
    () => getPrimaryArray(data, ["items", "users", "rows"]),
    [data]
  );
  const summaryEntries = useMemo(() => getSummaryEntries(data), [data]);

  const onApply = (event) => {
    event.preventDefault();
    setQuery(draft);
  };

  return (
    <section className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
      <div>
        <h3 className="text-2xl font-semibold text-slate-900">Activation Report</h3>
        <p className="mt-1 text-sm text-slate-500">
          Identify signed-up learners who have not started a section within the selected time window.
        </p>
      </div>

      <form
        onSubmit={onApply}
        className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4"
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,280px)_1fr_auto] xl:items-end">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Days Without Start
          </span>
          <input
            type="number"
            min={1}
            max={90}
            value={draft.days_without_start}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, days_without_start: Number(e.target.value) }))
            }
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
          />
        </label>

          <PaginationControls
            limit={draft.limit}
            offset={draft.offset}
            onLimitChange={(value) =>
              setDraft((prev) => ({ ...prev, limit: Math.max(1, value || 1) }))
            }
            onOffsetChange={(value) =>
              setDraft((prev) => ({ ...prev, offset: Math.max(0, value || 0) }))
            }
            disabled={loading}
            disableNext={users.length < (draft.limit || 1)}
          />

          <button
            type="submit"
            className="min-w-[160px] rounded-xl bg-[#54B0AF] px-5 py-2.5 text-sm font-medium text-white shadow hover:opacity-90"
            disabled={loading}
          >
            Apply
          </button>
        </div>
      </form>

      {loading && <SectionLoading text="Loading activation report..." />}
      {error && <SectionError message={error} />}

      {!loading && !error && (
        <div className="space-y-4" data-testid="activation-report-content">
          <SummaryCards entries={summaryEntries} />
          <DataTable
            title="Users"
            subtitle="Learners who signed up but have not started any section yet."
            rows={users}
            columns={USER_COLUMNS}
            emptyText="No users found for current filters."
          />
        </div>
      )}
    </section>
  );
};

export default ActivationReportPanel;

